/**
 * PortalAccessFields - user-level access switches.
 *
 * Two groups:
 *   1. Portals      (Income Tax / GST / TDS)
 *   2. Capabilities (per portal, since 2026-07-15):
 *        - Response Generation                 (IT / GST / TDS)
 *        - Prepare Appeal                      (IT / GST)
 *        - All Client Access for Prepare Appeal(IT / GST)
 *
 * Shared by super_admin, partner_admin and tenant_admin user-edit forms.
 *
 * Designed to be **dumb**: takes a value object and an onChange callback
 * so the parent owns the state shape (matches existing patterns in the
 * Admin pages where forms hold a `formData` map).
 *
 * Value shape (all booleans):
 *   enable_income_tax_portal / enable_gst_portal / enable_tds_portal
 *   enable_response_generation_income_tax / _gst / _tds
 *   enable_prepare_appeal_income_tax / _gst
 *   enable_prepare_appeal_all_clients_income_tax / _gst
 *
 * Gate model: each switch is the USER half of an AND. The ORG half lives
 * elsewhere (org portal flags for portals; plan features
 * AI_RESPONSE_GENERATION / *_APPEAL_DRAFTING for capabilities). A user
 * only gets a portal/capability when BOTH halves are on. Prepare Appeal
 * exists only for IT + GST (there is no TDS appeals feature), so the
 * appeal rows have no TDS column.
 */
import React from "react";
import { Switch } from "antd";
import { APPEAL_FEATURE_ENABLED } from "../utils/constants";

const PORTALS = [
  { field: "enable_income_tax_portal", label: "Income Tax", colour: "#1d4ed8" },
  { field: "enable_gst_portal",        label: "GST",        colour: "#7c3aed" },
  { field: "enable_tds_portal",        label: "TDS",        colour: "#db2777" },
];

// Portal columns for the per-portal capability rows.
const PORTAL_COLS = [
  { key: "income_tax", label: "Income Tax", colour: "#1d4ed8" },
  { key: "gst",        label: "GST",        colour: "#7c3aed" },
  { key: "tds",        label: "TDS",        colour: "#db2777" },
];

// Each capability + which portals it applies to + how to build the field key.
// The two Prepare-Appeal rows are dropped entirely while the Appeal feature is
// switched off (APPEAL_FEATURE_ENABLED — the same master switch that hides the
// Prepare Appeal buttons/routes and unmounts the appeal APIs). Showing toggles
// for a capability the product does not currently expose only invites an admin
// to grant access that leads nowhere. The underlying per-user flags are left
// untouched in the database, so re-enabling the feature restores each user's
// existing choices exactly.
const ALL_CAPABILITIES = [
  {
    label: "Response Generation",
    hint: "Generate AI responses to notices",
    portals: ["income_tax", "gst", "tds"],
    fieldFor: (p) => `enable_response_generation_${p}`,
  },
  {
    label: "Prepare Appeal",
    hint: "Draft appeals from an order",
    portals: ["income_tax", "gst"],
    fieldFor: (p) => `enable_prepare_appeal_${p}`,
    appealOnly: true,
  },
  {
    label: "All Client Access for Prepare Appeal",
    hint: "Draft appeals for every client in the portal, not just assigned or shared clients",
    portals: ["income_tax", "gst"],
    fieldFor: (p) => `enable_prepare_appeal_all_clients_${p}`,
    appealOnly: true,
  },
];

const CAPABILITIES = ALL_CAPABILITIES.filter(
  (c) => APPEAL_FEATURE_ENABLED || !c.appealOnly,
);

const PortalAccessFields = ({
  value = {},
  onChange,
  disabled = false,
  // Optional Set of field keys (e.g. `new Set(["enable_tds_portal"])`)
  // to disable individually, on top of the all-or-nothing `disabled`
  // prop. Used by the admin user-edit drawer to lock toggles for
  // portals the org itself has disabled (UI enforcement of the
  // runtime user-AND-org gate).
  disabledFields,
  layout = "horizontal",
  // Hide the capability group entirely (e.g. on forms that only scope
  // portals). Defaults to showing it.
  showCapabilities = true,
}) => {
  const isFieldDisabled = (field) =>
    disabled || (disabledFields && disabledFields.has(field));
  const handle = (field) => (next) => {
    if (isFieldDisabled(field)) return;
    onChange?.({ ...value, [field]: next });
  };

  const vertical = layout === "vertical";

  // --- Portal switch row (unchanged) ---------------------------------------
  const renderPortalRow = (item) => (
    <div
      key={item.field}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: value[item.field] ? "#f0f7ff" : "#fafafa",
        transition: "background 0.15s ease",
        justifyContent: vertical ? "space-between" : "flex-start",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 8,
            background: item.colour,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 500, minWidth: 70 }}>
          {item.label}
        </span>
      </span>
      <Switch
        size="small"
        checked={!!value[item.field]}
        onChange={handle(item.field)}
        disabled={isFieldDisabled(item.field)}
      />
    </div>
  );

  // --- Per-portal capability row -------------------------------------------
  // One card per capability: the capability's name + hint, then a small
  // labelled switch for each portal the capability applies to.
  const renderCapabilityRow = (cap) => (
    <div
      key={cap.label}
      style={{
        padding: "10px 12px",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#fafafa",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{cap.label}</span>
        {cap.hint && (
          <span style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.3 }}>
            {cap.hint}
          </span>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {cap.portals.map((portalKey) => {
          const col = PORTAL_COLS.find((c) => c.key === portalKey);
          const field = cap.fieldFor(portalKey);
          return (
            <span
              key={field}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: value[field] ? "#f0f7ff" : "#fff",
                transition: "background 0.15s ease",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 7,
                  background: col?.colour || "#8c8c8c",
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 12, fontWeight: 500, minWidth: 62 }}>
                {col?.label || portalKey}
              </span>
              <Switch
                size="small"
                checked={!!value[field]}
                onChange={handle(field)}
                disabled={isFieldDisabled(field)}
              />
            </span>
          );
        })}
      </div>
    </div>
  );

  const portalGroupStyle = {
    display: "flex",
    flexDirection: vertical ? "column" : "row",
    gap: vertical ? 12 : 24,
    flexWrap: "wrap",
  };

  const headingStyle = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "#8c8c8c",
    marginBottom: 8,
  };

  if (!showCapabilities) {
    return <div style={portalGroupStyle}>{PORTALS.map(renderPortalRow)}</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <div style={headingStyle}>Portals</div>
        <div style={portalGroupStyle}>{PORTALS.map(renderPortalRow)}</div>
      </div>
      <div>
        <div style={headingStyle}>Capabilities (per portal)</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {CAPABILITIES.map(renderCapabilityRow)}
        </div>
      </div>
    </div>
  );
};

export default PortalAccessFields;
